type APIGateway = {
  REGION: string
  URL: string
}

type Cognito = {
  REGION: string
  USER_POOL_ID: string
  APP_CLIENT_ID: string
  IDENTITY_POOL_ID: string
}

type Appsync = {
  REGION: string
  ENDPOINT: string
}

type EnablementTier = {
  id: string
  title: string
  primaryHeader?: string
  subheader?: string
  price: string
  description: string[]
  buttonText: string
  buttonVariant: string
  frequency: string
}

type CommissionedTier = {
  id: string
  title: string
  primaryHeader?: string
  subheader?: string
  price: string
  description: string[]
  buttonText: string
  buttonVariant: string
  frequency: string
}

type StripeProducts = {
  enablementTiers: EnablementTier[]
  commissionedTiers: CommissionedTier[]
}

type GoogleAnalytics = {
  enabled: boolean
  trackingCode: string
}

type Config = {
  apiGateway: APIGateway
  cognito: Cognito
  appsync: Appsync
  STRIPE_PUBLIC_KEY: string
  STRIPE_PRODUCTS: StripeProducts
  MAV_BASE_URL: string
  APP_BASE_URL?: string
  allowBrowserView: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  STRIPE_V2?: any
  googleAnalytics: GoogleAnalytics
}

const lite = {
  primaryHeader: 'Lite',
  importedLeads:
    'Prospect RECON™ ZERO- Prospects identified, researched, and delivered to you – Monthly',
  templates: 6,
  sends: 'Unlimited',
  onboarding: 'Basic',
  templateAssist: 'Access to Template Assist',
  wingmanAccess: 'our Success Wingman™ Team',
}

// trigger build
const pro = {
  primaryHeader: 'Pro',
  importedLeads:
    'Prospect RECON™ 400- Prospects identified, researched, and delivered to you – Monthly',
  templates: 12,
  sends: 'Unlimited',
  onboarding: 'Pro',
  templateAssist: 'Access to Template Assist',
  wingmanAccess: 'Dedicated Success Wingman™',
}

const enterprise = {
  primaryHeader: 'Enterprise',
  importedLeads:
    'Prospect RECON™ 700- Prospects identified, researched, and delivered to you – Monthly',
  templates: 24,
  sends: 'Unlimited',
  onboarding: 'Concierge',
  templateAssist: 'Content Consulting Services',
  wingmanAccess: 'Concierge Success Wingman™',
}

const envs = {
  dev: {
    Monthly: [
      {
        ...lite,
        id: 'price_1HqOMiBQ2V0NM2d5qw6af6GO',
        price: 298,
      },
      {
        ...pro,
        id: 'price_1HqONfBQ2V0NM2d55Xccobq9',
        price: 498,
      },
      {
        ...enterprise,
        id: 'price_1HqOOPBQ2V0NM2d59LXajCrd',
        price: 798,
      },
    ],
    '6 Month': [
      {
        ...lite,
        id: 'price_1HqOMiBQ2V0NM2d5JjIytmvn',
        price: 109,
        stripeDescription: 'when you pre-pay $654 for 6 months',
      },
      {
        ...pro,
        id: 'price_1HqONfBQ2V0NM2d5WjpeJ5SS',
        price: 179,
        stripeDescription: 'when you pre-pay $1074 for 6 months',
      },
      {
        ...enterprise,
        id: 'price_1HqOOPBQ2V0NM2d5q0irT51g',
        price: 229,
        stripeDescription: 'when you pre-pay $1374 for 6 months',
      },
    ],
    Yearly: [
      {
        ...lite,
        id: 'price_1HqOMiBQ2V0NM2d5TW7dSBQX',
        price: 225,
        stripeDescription: 'when you pre-pay $948 for 12 months',
      },
      {
        ...pro,
        id: 'price_1HqONfBQ2V0NM2d5KjnOfDSV',
        price: 425,
        stripeDescription: 'when you pre-pay $1788 for 12 months',
      },
      {
        ...enterprise,
        id: 'price_1HqOOPBQ2V0NM2d5pg1IrztJ',
        price: 725,
        stripeDescription: 'when you pre-pay $2388 for 12 months',
      },
    ],
  },
  beta: {
    Monthly: [
      {
        ...lite,
        id: 'price_1HDG4xBQ2V0NM2d53F9kGW12',
        price: 298,
      },
      {
        ...pro,
        id: 'price_1HDGABBQ2V0NM2d5gU6ZvPJB',
        price: 498,
      },
      {
        ...enterprise,
        id: 'price_1HDG8QBQ2V0NM2d5tsHxwLc4',
        price: 798,
      },
    ],
    '6 Month': [
      {
        ...lite,
        id: 'price_1HDG4xBQ2V0NM2d5QHGH5r7M',
        price: 109,
        stripeDescription: 'when you pre-pay $654 for 6 months',
      },
      {
        ...pro,
        id: 'price_1HDGACBQ2V0NM2d5Lv36u2jp',
        price: 179,
        stripeDescription: 'when you pre-pay $1074 for 6 months',
      },
      {
        ...enterprise,
        id: 'price_1HDG8QBQ2V0NM2d5JGjdmWqA',
        price: 229,
        stripeDescription: 'when you pre-pay $1374 for 6 months',
      },
    ],
    Yearly: [
      {
        ...lite,
        id: 'price_1HDG4xBQ2V0NM2d5pyejjkzU',
        price: 225,
        stripeDescription: 'when you pre-pay $948 for 12 months',
      },
      {
        ...pro,
        id: 'price_1HDGACBQ2V0NM2d5JkiqafFL',
        price: 425,
        stripeDescription: 'when you pre-pay $1788 for 12 months',
      },
      {
        ...enterprise,
        id: 'price_1HDG8QBQ2V0NM2d5155NitXQ',
        price: 725,
        stripeDescription: 'when you pre-pay $2388 for 12 months',
      },
    ],
  },
}

const dev: Config = {
  apiGateway: {
    REGION: 'us-east-1',
    URL: 'https://2rv87dit7f.execute-api.us-east-1.amazonaws.com/dev',
  },
  cognito: {
    REGION: 'us-east-1',
    USER_POOL_ID: 'us-east-1_rVPxshF63',
    APP_CLIENT_ID: '2mt9kuno8lgu5k1n96u0vpqi2t',
    IDENTITY_POOL_ID: 'us-east-1:ead22611-2679-49fc-b3d4-34bde9dd61d6',
  },
  appsync: {
    REGION: 'us-east-1',
    ENDPOINT: 'https://4sz4fhbvn5b3nejefylycxjtvm.appsync-api.us-east-1.amazonaws.com/graphql',
  },
  STRIPE_PUBLIC_KEY: 'pk_test_IrZZOy1gEHHQQtrdryXO66IN001RozEI0L',
  STRIPE_PRODUCTS: {
    enablementTiers: [
      {
        id: 'plan_H2JGW7KGxgtTO2',
        title: 'Prospect RECON™',
        primaryHeader: 'Lite',
        description: [
          '215 identified, researched, and delivered prospects – Per Month',
          '6 pre-built, fully customizable missions',
          'Unlimited hangar imports',
          'No sending cap',
          'Access to our Success Wingman™ Team',
          'Technical Support',
        ],
        price: '109',
        buttonText: 'Get started',
        buttonVariant: 'outlined',
        frequency: '/mo',
      },
      {
        id: 'plan_H2JGttpedvqiX4',
        title: 'Semi Annually',
        subheader: 'Most popular',
        description: [
          '20 users included',
          '10 GB of storage',
          'Help center access',
          'Priority email support',
        ],
        price: '109',
        buttonText: 'Get started',
        buttonVariant: 'contained',
        frequency: '/6 mo',
      },
      {
        id: 'plan_H2JFe4RSRIjMfT',
        title: 'Annually',
        price: '1200',
        description: [
          '50 users included',
          '30 GB of storage',
          'Help center access',
          'Phone & email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'outlined',
        frequency: '/year',
      },
    ],
    commissionedTiers: [
      {
        id: 'plan_H2JFpXD6kTeSbg',
        title: 'Monthly',
        price: '200',
        description: [
          '10 users included',
          '2 GB of storage',
          'Help center access',
          'Email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'outlined',
        frequency: '/mo',
      },
      {
        id: 'plan_H2JF8uU4kb21rc',
        title: 'Semi Annually',
        subheader: 'Most popular',
        price: '1050',
        description: [
          '20 users included',
          '10 GB of storage',
          'Help center access',
          'Priority email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'contained',
        frequency: '/6 mo',
      },
      {
        id: 'plan_H2JEQJU1YZ1ecR',
        title: 'Annually',
        price: '1800',
        description: [
          '50 users included',
          '30 GB of storage',
          'Help center access',
          'Phone & email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'outlined',
        frequency: '/year',
      },
    ],
  },
  STRIPE_V2: { ...envs.dev },
  googleAnalytics: {
    enabled: true,
    trackingCode: 'UA-146423230-6',
  },
  MAV_BASE_URL: 'https://api.maverickapp.io/dev',
  APP_BASE_URL: 'https://app-dev.maverickapp.io',
  allowBrowserView: true,
}

const beta: Config = {
  apiGateway: {
    REGION: 'us-east-1',
    URL: 'https://7knkcdvuj6.execute-api.us-east-1.amazonaws.com/beta',
  },
  cognito: {
    REGION: 'us-east-1',
    USER_POOL_ID: 'us-east-1_WL8xPR6TO',
    APP_CLIENT_ID: '2qhvl5kpe4tc7v20stt0smel2h',
    IDENTITY_POOL_ID: 'us-east-1:1f69ba5d-00cc-4abd-a533-2e49408bec69',
  },
  appsync: {
    REGION: 'us-east-1',
    ENDPOINT: 'https://23dlru62qnbohpcxovmshjepda.appsync-api.us-east-1.amazonaws.com/graphql',
  },
  STRIPE_PUBLIC_KEY: 'pk_live_QpjmYsQ9wLIwQMzaaGbtVbRZ007x3HGTbl',
  STRIPE_PRODUCTS: {
    enablementTiers: [
      {
        id: 'plan_GyAGa6ZVwORRVy',
        title: 'Monthly',
        price: '150',
        description: [
          '10 users included',
          '2 GB of storage',
          'Help center access',
          'Email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'outlined',
        frequency: '/mo',
      },
      {
        id: 'plan_GyAHlhF1kEOVQq',
        title: 'Semi Annually',
        subheader: 'Most popular',
        price: '750',
        description: [
          '20 users included',
          '10 GB of storage',
          'Help center access',
          'Priority email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'contained',
        frequency: '/6 mo',
      },
      {
        id: 'plan_GyAIklDMmcAd8x',
        title: 'Annually',
        price: '1200',
        description: [
          '50 users included',
          '30 GB of storage',
          'Help center access',
          'Phone & email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'outlined',
        frequency: '/year',
      },
    ],
    commissionedTiers: [
      {
        id: 'plan_GyAK7eLnJU60ri',
        title: 'Monthly',
        price: '200',
        description: [
          '10 users included',
          '2 GB of storage',
          'Help center access',
          'Email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'outlined',
        frequency: '/mo',
      },
      {
        id: 'plan_GyALDrvIzJ0Fim',
        title: 'Semi Annually',
        subheader: 'Most popular',
        price: '1050',
        description: [
          '20 users included',
          '10 GB of storage',
          'Help center access',
          'Priority email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'contained',
        frequency: '/6 mo',
      },
      {
        id: 'plan_GyALIUHIesTA9j',
        title: 'Annually',
        price: '1800',
        description: [
          '50 users included',
          '30 GB of storage',
          'Help center access',
          'Phone & email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'outlined',
        frequency: '/year',
      },
    ],
  },
  STRIPE_V2: { ...envs.beta },
  googleAnalytics: {
    enabled: true,
    trackingCode: 'UA-146423230-2',
  },
  MAV_BASE_URL: 'https://api.maverickapp.io/beta',
  APP_BASE_URL: 'https://cockpit.maverickapp.io',
  allowBrowserView: false,
}

const prod: Config = {
  apiGateway: {
    REGION: 'us-east-1',
    URL: 'https://2rv87dit7f.execute-api.us-east-1.amazonaws.com',
  },
  cognito: {
    REGION: 'us-east-1',
    USER_POOL_ID: 'us-east-1_Joc6wmiXN',
    APP_CLIENT_ID: '367vor91tce81c8oaloo7u9doj',
    IDENTITY_POOL_ID: 'us-east-1:db91ec45-fc37-4fe6-8c7e-a60cf63938d6',
  },
  appsync: {
    REGION: 'us-east-1',
    ENDPOINT: 'https://7go3lu52tzfztptjxe4kq6ayhq.appsync-api.us-east-1.amazonaws.com/graphql',
  },
  STRIPE_PUBLIC_KEY: 'pk_live_QpjmYsQ9wLIwQMzaaGbtVbRZ007x3HGTbl',
  STRIPE_PRODUCTS: {
    enablementTiers: [
      {
        id: 'plan_GyAGa6ZVwORRVy',
        title: 'Monthly',
        price: '150',
        description: [
          '10 users included',
          '2 GB of storage',
          'Help center access',
          'Email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'outlined',
        frequency: '/mo',
      },
      {
        id: 'plan_GyAHlhF1kEOVQq',
        title: 'Semi Annually',
        subheader: 'Most popular',
        price: '750',
        description: [
          '20 users included',
          '10 GB of storage',
          'Help center access',
          'Priority email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'contained',
        frequency: '/6 mo',
      },
      {
        id: 'plan_GyAIklDMmcAd8x',
        title: 'Annually',
        price: '1200',
        description: [
          '50 users included',
          '30 GB of storage',
          'Help center access',
          'Phone & email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'outlined',
        frequency: '/year',
      },
    ],
    commissionedTiers: [
      {
        id: 'plan_GyAK7eLnJU60ri',
        title: 'Monthly',
        price: '200',
        description: [
          '10 users included',
          '2 GB of storage',
          'Help center access',
          'Email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'outlined',
        frequency: '/mo',
      },
      {
        id: 'plan_GyALDrvIzJ0Fim',
        title: 'Semi Annually',
        subheader: 'Most popular',
        price: '1050',
        description: [
          '20 users included',
          '10 GB of storage',
          'Help center access',
          'Priority email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'contained',
        frequency: '/6 mo',
      },
      {
        id: 'plan_GyALIUHIesTA9j',
        title: 'Annually',
        price: '1800',
        description: [
          '50 users included',
          '30 GB of storage',
          'Help center access',
          'Phone & email support',
        ],
        buttonText: 'Get started',
        buttonVariant: 'outlined',
        frequency: '/year',
      },
    ],
  },
  STRIPE_V2: { ...envs.beta },
  googleAnalytics: {
    enabled: true,
    trackingCode: 'UA-146423230-2',
  },
  MAV_BASE_URL: 'https://api.maverickapp.io/prod',
  APP_BASE_URL: 'https://app.maverickapp.io',
  allowBrowserView: false,
}

export const analyticsConfig = {
  AWSPinpoint: {
    appId: 'bfdc0545dea646bbb3fa424536450576',
    region: 'us-east-1',
    mandatorySignIn: false,
  },
}

// Default to dev if not set
export const getConfig = (): Config => {
  switch (process.env.REACT_APP_STAGE) {
    case 'prod':
      // eslint-disable-next-line no-console
      console.log = (): void => {}
      return prod
    case 'beta':
      // eslint-disable-next-line no-console
      console.log = (): void => {}
      return beta
    default:
      return {
        ...dev,
        allowBrowserView: true,
      }
  }
}

export default {
  // Add common config values here
  MAX_ATTACHMENT_SIZE: 5000000,
  ...getConfig(),
}
